import client from "./client";
import { normalizeRole } from "./rolesApi";
import { publishCollectionChange } from "../utils/collectionSync";
import { createOfflineUuid, enqueuePendingWrite, isOffline } from "./offlineMutationQueue";
import { getSharedRecords, removeSharedRecord, upsertSharedRecord, upsertSharedRecords } from "./sharedRecordCache";

const COLLECTION_KEY = "role-assignments";

function toNullIfBlank(value) {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

function normalizeBoolean(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return Boolean(value);
}

function unwrapPayload(responseData) {
  const envelope = responseData ?? {};
  return envelope.data ?? envelope;
}

function normalizeAssignment(row = {}) {
  const user = row.user ?? {};
  const institution = row.institution ?? {};
  const role = normalizeRole(row.role ?? {});
  const assignedByUuid = row.assigned_by_uuid ?? row.assignedByUuid ?? null;

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    user_uuid: user.uuid ?? row.user_uuid ?? row.user_id ?? null,
    user_id: user.uuid ?? row.user_uuid ?? row.user_id ?? null,
    user_label:
      user.name ??
      user.email ??
      row.user_name ??
      row.user_email ??
      row.user_label ??
      row.user_uuid ??
      row.user_id ??
      "—",
    role_uuid: role.uuid ?? row.role_uuid ?? row.role_id ?? null,
    role_id: role.uuid ?? row.role_uuid ?? row.role_id ?? null,
    role_code: role.code ?? row.role_code ?? null,
    role_label:
      role.name ??
      row.role_name ??
      row.role_label ??
      role.code ??
      null,
    role,
    institution_uuid: institution.uuid ?? row.institution_uuid ?? row.institution_id ?? null,
    institution_id: institution.uuid ?? row.institution_uuid ?? row.institution_id ?? null,
    institution_label:
      institution.name ??
      row.institution_name ??
      row.institution_label ??
      row.institution_uuid ??
      row.institution_id ??
      "All institutions",
    module_scope: row.module_scope ?? row.moduleScope ?? null,
    moduleScope: row.moduleScope ?? row.module_scope ?? null,
    assigned_by_uuid: assignedByUuid,
    assigned_by: row.assigned_by ?? row.assignedBy ?? assignedByUuid ?? null,
    expires_at: row.expires_at ?? row.expiresAt ?? null,
    is_active: normalizeBoolean(row.is_active ?? row.isActive, true),
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
  };
}

function extractAssignmentList(responseData) {
  const payload = unwrapPayload(responseData);
  const items =
    Array.isArray(payload) ? payload :
    Array.isArray(payload.items) ? payload.items :
    Array.isArray(payload.results) ? payload.results :
    [];

  return items.map(normalizeAssignment);
}

function extractAssignmentRecord(responseData) {
  const payload = unwrapPayload(responseData);
  if (Array.isArray(payload)) return normalizeAssignment(payload[0] ?? {});
  if (payload?.item) return normalizeAssignment(payload.item);
  if (payload?.items && Array.isArray(payload.items)) {
    return normalizeAssignment(payload.items[0] ?? {});
  }
  return normalizeAssignment(payload);
}

function findCachedAssignment(uuid) {
  const key = String(uuid ?? '').trim();
  if (!key) return null;

  return getSharedRecords(COLLECTION_KEY).find((row) => String(row?.uuid ?? row?.id ?? '').trim() === key) ?? null;
}

function buildAssignmentPayload(payload = {}) {
  return {
    user_uuid:        toNullIfBlank(payload.user_uuid),
    role_code:        toNullIfBlank(payload.role_code),
    institution_uuid: toNullIfBlank(payload.institution_uuid),
    module_scope:     toNullIfBlank(payload.module_scope ?? payload.moduleScope),
  };
}

export const roleAssignmentsApi = {
  async list(params = {}) {
    try {
      const { data } = await client.get("/v1/role-assignments", { params });
      const items = extractAssignmentList(data);
      upsertSharedRecords(COLLECTION_KEY, items);
      return items;
    } catch (error) {
      if (isOffline()) {
        return getSharedRecords(COLLECTION_KEY).map(normalizeAssignment);
      }
      throw error;
    }
  },

  async get(uuid) {
    try {
      const { data } = await client.get(`/v1/role-assignments/${uuid}`);
      const item = extractAssignmentRecord(data);
      if (item?.uuid || item?.id) {
        upsertSharedRecord(COLLECTION_KEY, item);
      }
      return item;
    } catch (error) {
      if (isOffline()) {
        const cached = findCachedAssignment(uuid);
        if (cached) return normalizeAssignment(cached);
        return normalizeAssignment({ uuid, id: uuid });
      }
      throw error;
    }
  },

  async create(payload) {
    const body = buildAssignmentPayload(payload);

    if (isOffline()) {
      const tempUuid = createOfflineUuid(COLLECTION_KEY);
      const optimistic = normalizeAssignment({
        uuid: tempUuid,
        id: tempUuid,
        user: body.user_uuid ? { uuid: body.user_uuid, name: body.user_uuid } : null,
        user_uuid: body.user_uuid,
        role: body.role_code ? { code: body.role_code, name: body.role_code } : null,
        role_code: body.role_code,
        institution: body.institution_uuid ? { uuid: body.institution_uuid, name: body.institution_uuid } : null,
        institution_uuid: body.institution_uuid,
        module_scope: body.module_scope,
        is_active: true,
      });
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      enqueuePendingWrite({
        collectionKey: COLLECTION_KEY,
        action: "create",
        method: "post",
        url: "/v1/role-assignments",
        data: body,
        tempUuid,
        recordUuid: tempUuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: "create", uuid: tempUuid, offline: true });
      return optimistic;
    }

    const { data } = await client.post("/v1/role-assignments", body);
    const item = extractAssignmentRecord(data);
    if (item?.uuid || item?.id) {
      upsertSharedRecord(COLLECTION_KEY, item);
    }
    publishCollectionChange(COLLECTION_KEY, { action: "create", uuid: item?.uuid ?? null });
    return item;
  },

  // The backend does not support PUT /v1/role-assignments/{uuid}.
  // Updating a role assignment is done by deleting the old one and creating
  // a new one with the revised values.
  async update(uuid, payload) {
    const body = buildAssignmentPayload(payload);

    if (isOffline()) {
      const existing = findCachedAssignment(uuid);
      const optimistic = normalizeAssignment({
        ...(existing ?? {}),
        uuid,
        id: uuid,
        user: body.user_uuid ? { uuid: body.user_uuid, name: body.user_uuid } : existing?.user ?? null,
        user_uuid: body.user_uuid ?? existing?.user_uuid ?? existing?.user_id ?? null,
        role: body.role_code ? { code: body.role_code, name: body.role_code } : existing?.role ?? null,
        role_code: body.role_code ?? existing?.role_code ?? null,
        institution: body.institution_uuid ? { uuid: body.institution_uuid, name: body.institution_uuid } : existing?.institution ?? null,
        institution_uuid: body.institution_uuid ?? existing?.institution_uuid ?? null,
        module_scope: body.module_scope ?? existing?.module_scope ?? null,
        is_active: existing?.is_active ?? true,
      });
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      enqueuePendingWrite({
        collectionKey: COLLECTION_KEY,
        action: "remove",
        method: "delete",
        url: `/v1/role-assignments/${uuid}`,
        recordUuid: uuid,
      });
      enqueuePendingWrite({
        collectionKey: COLLECTION_KEY,
        action: "create",
        method: "post",
        url: "/v1/role-assignments",
        data: body,
        tempUuid: uuid,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: "update", uuid, offline: true });
      return optimistic;
    }

    await client.delete(`/v1/role-assignments/${uuid}`);
    const { data } = await client.post("/v1/role-assignments", body);
    const item = extractAssignmentRecord(data);
    if (item?.uuid || item?.id) {
      upsertSharedRecord(COLLECTION_KEY, item);
    }
    publishCollectionChange(COLLECTION_KEY, { action: "update", uuid: item?.uuid ?? uuid });
    return item;
  },

  async remove(uuid) {
    if (isOffline()) {
      removeSharedRecord(COLLECTION_KEY, uuid);
      enqueuePendingWrite({
        collectionKey: COLLECTION_KEY,
        action: "remove",
        method: "delete",
        url: `/v1/role-assignments/${uuid}`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: "remove", uuid, offline: true });
      return { uuid, removed: true, offline: true };
    }

    const { data } = await client.delete(`/v1/role-assignments/${uuid}`);
    removeSharedRecord(COLLECTION_KEY, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: "remove", uuid });
    return unwrapPayload(data);
  },
};

export { normalizeAssignment, buildAssignmentPayload };
